import React, { useState, useEffect } from "react";
import {
  Home,
  ArrowLeft,
  Search,
  Compass,
  RefreshCw,
  Mail,
} from "lucide-react";

const NotFound = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [animatedNumbers, setAnimatedNumbers] = useState({
    first: 0,
    second: 0,
    third: 0,
  });

  // Animate the 404 numbers on mount
  useEffect(() => {
    const animateNumbers = () => {
      const duration = 1000;
      const start = Date.now();

      const animate = () => {
        const elapsed = Date.now() - start;
        const progress = Math.min(elapsed / duration, 1);

        // Easing function for smooth animation
        const easeOut = 1 - Math.pow(1 - progress, 3);

        setAnimatedNumbers({
          first: Math.floor(easeOut * 4),
          second: Math.floor(easeOut * 0),
          third: Math.floor(easeOut * 4),
        });

        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };

      requestAnimationFrame(animate);
    };

    const timer = setTimeout(animateNumbers, 300);
    return () => clearTimeout(timer);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    
    console.log("Searching for:", searchQuery);
  };

  const handleGoBack = () => {
    window.history.back();
  };

  const handleGoHome = () => {
    window.location.href = "/";
  };

  return (
    <div className="min-h-dvh bg-gradient-to-br from-black via-slate-800/10 to-slate-900/20 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-20 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-purple-500/5 rounded-full blur-3xl animate-pulse delay-500"></div>
      </div>

      {/* Floating elements */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className={`absolute w-2 h-2 bg-blue-400/20 rounded-full animate-pulse`}
            style={{
              left: `${20 + i * 15}%`,
              top: `${30 + i * 8}%`,
              animationDelay: `${i * 0.5}s`,
              animationDuration: `${3 + i * 0.5}s`,
            }}
          ></div>
        ))}
      </div>

      <div className="relative z-10 min-h-screen flex items-center justify-center px-4">
        <div className="max-w-4xl mx-auto text-center">
          {/* 404 Number Display */}
          <div className="mb-8">
            <div className="flex items-center justify-center space-x-4 mb-4">
              <span className="text-9xl md:text-[12rem] font-bold text-transparent bg-clip-text bg-gradient-to-r from-gold via-gold to-secondary leading-none transform transition-all duration-1000 hover:scale-105">
                {animatedNumbers.first}
              </span>
              <span className="text-9xl md:text-[12rem] font-bold text-transparent bg-clip-text bg-gradient-to-r from-gold via-gold to-secondary leading-none transform transition-all duration-1000 hover:scale-105 delay-200">
                {animatedNumbers.second}
              </span>
              <span className="text-9xl md:text-[12rem] font-bold text-transparent bg-clip-text bg-gradient-to-r from-gold via-gold to-secondary leading-none transform transition-all duration-1000 hover:scale-105 delay-400">
                {animatedNumbers.third}
              </span>
            </div>
            <div className="h-1 w-32 mx-auto bg-gradient-to-r from-gold via-gold to-secondary rounded-full"></div>
          </div>

          {/* Main Content */}
          <div className="space-y-6 mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Oops! Page Not Found
            </h1>
            <p className="text-base md:text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed">
              The page you're looking for seems to have vanished into the
              digital void. Don't worry though, we'll help you find your way
              back to where you need to be.
            </p>
          </div>

          {/* Search Section */}
          {/* <div className="mb-12">
            <form onSubmit={handleSearch} className="max-w-md mx-auto">
              <div className="relative">
                <div
                  className={`absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl blur-sm transition-opacity duration-300 ${
                    isSearchFocused ? "opacity-30" : "opacity-0"
                  }`}
                ></div>
                <div className="relative bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-1">
                  <div className="flex items-center">
                    <Search className="w-5 h-5 text-slate-400 ml-4" />
                    <input
                      type="text"
                      placeholder="Search for something else..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onFocus={() => setIsSearchFocused(true)}
                      onBlur={() => setIsSearchFocused(false)}
                      className="flex-1 bg-transparent border-none outline-none text-white placeholder-slate-400 px-4 py-3 text-lg"
                    />
                    <button
                      type="submit"
                      className="bg-gradient-to-r from-blue-500 to-purple-500 text-white px-6 py-2 rounded-lg font-semibold hover:from-blue-600 hover:to-purple-600 transition-all duration-200 transform hover:scale-105 active:scale-95"
                    >
                      Search
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div> */}

          {/* Action Buttons */}
          <div className="flex flex-row items-center justify-center gap-4 mb-8">
            <button
              onClick={handleGoHome}
              className="group flex items-center space-x-3 bg-gradient-to-r from-gold to-secondary hover:from-gold hover:to-secondary text-slate-900 px-8 py-4 rounded-xl font-semibold transition-all duration-200 transform hover:scale-105 active:scale-95 shadow-lg hover:shadow-gold/25 w-full md:w-auto"
            >
              <Home className="w-5 h-5 group-hover:rotate-12 transition-transform duration-200" />
              <span>Go Home</span>
            </button>

            <button
              onClick={handleGoBack}
              className="group flex items-center space-x-3 bg-slate-700/50 hover:bg-slate-600/50 text-white px-8 py-4 rounded-xl font-semibold transition-all duration-200 transform hover:scale-105 active:scale-95 backdrop-blur-sm border border-slate-600 hover:border-slate-500 w-full md:w-auto"
            >
              <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform duration-200" />
              <span>Go Back</span>
            </button>
          </div>

          {/* Additional Help Options */}
          <div className="hidden md:flex flex-row items-center justify-center gap-6 text-slate-400 text-sm">
            <button className="group flex items-center space-x-2 hover:text-blue-400 transition-colors duration-200">
              <Compass className="w-4 h-4 group-hover:rotate-45 transition-transform duration-300" />
              <span>Browse All Pages</span>
            </button>

            <div className="hidden md:block w-px h-4 bg-slate-600"></div>

            <button className="group flex items-center space-x-2 hover:text-purple-400 transition-colors duration-200">
              <RefreshCw className="w-4 h-4 group-hover:rotate-180 transition-transform duration-300" />
              <span>Try Refreshing</span>
            </button>

            <div className="hidden md:block w-px h-4 bg-slate-600"></div>

            <button className="group flex items-center space-x-2 hover:text-green-400 transition-colors duration-200">
              <Mail className="w-4 h-4 group-hover:scale-110 transition-transform duration-200" />
              <span>Contact Support</span>
            </button>
          </div>

          {/* Bottom decorative element */}
          <div className="mt-16 flex justify-center">
            <div className="flex space-x-2">
              {[...Array(3)].map((_, i) => (
                <div
                  key={i}
                  className="w-2 h-2 bg-gold rounded-full animate-bounce"
                  style={{
                    animationDelay: `${i * 0.2}s`,
                    animationDuration: "1.5s",
                  }}
                ></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotFound;
